// Voice console API layer. Each read hits the real (verified DB-backed) sk-orchestrator
// endpoint. On failure it returns an EMPTY value + live:false — never fabricated mock
// data — so screens render a truthful empty/error state. Identity comes from /v1/me.

import axiosInstance from '../../api/axiosInstance';
import {
  Voice_Controls_Intents_Url,
  Voice_Controls_Enrichment_Url,
  Voice_Controls_Settings_Url,
  Voice_Console_Followups_Url,
  Voice_Console_Followup_Url,
  Voice_Setup_Intents_Url,
  Voice_Setup_McpTools_Url,
  Voice_Setup_Intent_Url,
  Voice_Setup_Intent_Enrichment_Url,
  Voice_Setup_Intent_Versions_Url,
  Voice_Setup_Intent_CurrentVersion_Url,
  Voice_Setup_Router_Prompt_Url,
  Voice_Setup_Router_Prompt_Current_Url,
  Voice_Setup_Providers_Url,
  Voice_Setup_Providers_Default_Url,
  Voice_Setup_Providers_Override_Url,
  Platform_Admin_Orgs_Url,
  Voice_Admin_Plivo_Kycs_Url,
  Voice_Admin_Org_Number_Url,
  Voice_Admin_Org_Kyc_Link_Url,
  Voice_Calls_Url,
  Voice_Call_Url,
  Voice_Calls_Page_Url,
  Voice_Call_Insights_Url,
  Voice_Calls_Log_Summary_Url,
  Voice_Analytics_Console_Url,
  Voice_Board_Identity_Url,
  Voice_Board_Summary_Url,
} from '../../api/endpoints';
// Only shared TYPES + neutral defaults (PIPE_DEFAULT) + KPI/detail shapes (used via
// `typeof` for empty states) remain — the mock DATA rows are no longer imported.
import {
  Followup, CatalogIntent, OWNERS,
  PIPE_DEFAULT,
  CALL_KPIS, CALL_DETAIL_01, CallRow,
  ANALYTICS_KPIS, ANALYTICS_DIST,
} from '../../data/voiceMock';
import { timeAgo, fmtDur } from '../../utils/format';

// Backend catalog row (VoiceIntentSetupService.CatalogListItem). Owner display
// label is NOT sent — product display names are the FE's presentation concern
// (OWNERS map), so the backend never duplicates the platform product registry.
interface CatalogRow {
  id: string;
  slug: string;
  category: string;
  ownerKind: 'PRODUCT' | 'VERTICAL';
  ownerRef: string;
  description: string;
  displayName?: string | null;
  status: string;
  spineTool: string | null;
  enrichment: string[];
  currentVersionNo: number | null;
  tenantCount: number;
}
interface CatalogDetailRow extends CatalogRow {
  versions: { versionNo: number; isCurrent: boolean; intentPrompt: string; createdBy: string; createdAt: string }[];
  utterances: string[];
}

// Resolve a backend ownerRef (platform product slug, e.g. "cal") to the FE's
// owner key + display label. Falls back to a humanized label if unknown, so a
// new product the FE hasn't listed still renders sensibly.
function resolveOwner(ownerRef: string): { key: string; label: string } {
  const direct = OWNERS.find(o => o.key === ownerRef);
  if (direct) return { key: direct.key, label: direct.label };
  // Common platform-slug → FE-key aliases (backend uses 'cal', FE uses 'calendar').
  const alias: Record<string, string> = { cal: 'calendar', calendar: 'calendar' };
  const aliased = OWNERS.find(o => o.key === alias[ownerRef]);
  if (aliased) return { key: aliased.key, label: aliased.label };
  return { key: ownerRef, label: ownerRef.charAt(0).toUpperCase() + ownerRef.slice(1) };
}

// Backend catalog row → the FE CatalogIntent shape the screens render.
function toCatalogIntent(r: CatalogRow, detail?: CatalogDetailRow): CatalogIntent {
  const owner = resolveOwner(r.ownerRef);
  return {
    id: r.id, // carried so the editor drawer can call id-keyed setup endpoints
    slug: r.slug,
    displayName: r.displayName ?? null,
    category: r.category,
    ownerKind: r.ownerKind,
    owner: owner.key,
    ownerLabel: owner.label,
    spine: r.spineTool ?? '',
    enrich: r.enrichment,
    desc: r.description,
    tenants: r.tenantCount,
    status: r.status,
    ver: r.currentVersionNo ?? 0,
    utter: detail?.utterances ?? [],
    slots: [], // derived from spine schema; not surfaced by this read yet
  };
}

// Backend row shape (voice_followups). Names differ from the FE Followup type
// the design HTML uses, so we normalize here — one transform, one place.
interface FollowupRow {
  id: string;
  callId: string;
  reason: string;
  requestedIntent: string | null;
  status: 'OPEN' | 'RESOLVED' | 'DISMISSED';
  assignee: string | null;
  createdAt: string;
}

// Map a DB row → the FE Followup. `why` (reason-category) has no backend column
// yet, so it's left blank for real rows (the screen drops the empty segment) —
// no fabricated data, no duplication of a datum the backend doesn't own.
function toFollowup(r: FollowupRow): Followup {
  return {
    id: r.id,
    call: r.callId,
    reason: r.reason,
    intent: r.requestedIntent ?? '',
    why: '',
    status: r.status,
    assignee: r.assignee,
    age: timeAgo(r.createdAt),
  };
}

// Live read against the real orchestrator. Every backing route is implemented and
// DB-backed (verified), so there is NO mock fallback: on success → { data, live:true };
// on failure → the caller's EMPTY value + live:false, letting screens render a
// truthful empty/error state instead of fabricated demo data.
async function liveRead<T>(fn: () => Promise<T>, empty: T): Promise<{ data: T; live: boolean }> {
  try {
    const data = await fn();
    return { data, live: true };
  } catch {
    return { data: empty, live: false };
  }
}

// ── Controls: tenant intent toggles ──
export async function listTenantIntents() {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Controls_Intents_Url);
    return data?.data?.intents ?? data?.intents ?? [];
  }, [] as any[]);
}

export async function setIntentEnabled(intentId: string, enabled: boolean) {
  const { data } = await axiosInstance.put(Voice_Controls_Intents_Url, { intentId, enabled });
  return data;
}

export async function setEnrichment(intentId: string, toolName: string, on: boolean) {
  const { data } = await axiosInstance.put(Voice_Controls_Enrichment_Url, { intentId, toolName, on });
  return data;
}

// ── Console: follow-ups ──
export async function listFollowups(status?: string): Promise<{ data: Followup[]; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Console_Followups_Url, { params: status ? { status } : undefined });
    const rows = (data?.data?.followups ?? data?.followups ?? []) as FollowupRow[];
    return rows.map(toFollowup);
  }, [] as Followup[]);
}

// Count open follow-ups without pulling the rows. Home renders only the tally,
// and fetching every row to call .length on it made that tile scale with the
// backlog. `countOnly` asks the backend for a COUNT(*) instead.
//
// Falls back to the row count so this is safe against either deploy order: an
// older backend ignores the unknown query param and returns the full list, and
// we simply count it as before.
export async function getOpenFollowupCount(): Promise<{ data: number; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Console_Followups_Url, {
      params: { status: 'OPEN', countOnly: '1' },
    });
    const body = data?.data ?? data;
    if (typeof body?.count === 'number') return body.count;
    return (body?.followups ?? []).length as number;
  }, 0);
}

export async function updateFollowup(id: string, patch: { status?: string; assignee?: string | null }) {
  const { data } = await axiosInstance.patch(Voice_Console_Followup_Url(id), patch);
  return data;
}

// ── Setup: intent catalog list (Softknack) ──
export async function listCatalog(): Promise<{ data: CatalogIntent[]; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Setup_Intents_Url);
    const rows = (data?.data?.intents ?? data?.intents ?? []) as CatalogRow[];
    return rows.map(r => toCatalogIntent(r));
  }, [] as CatalogIntent[]);
}

export interface McpSlot { name: string; required: boolean; description: string }
export interface McpTool { name: string; role: 'SPINE' | 'ENRICHMENT' | 'BOTH'; slots?: McpSlot[] }
export async function listMcpTools(): Promise<{ data: McpTool[]; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Setup_McpTools_Url);
    return (data?.data?.tools ?? []) as McpTool[];
  }, [] as McpTool[]);
}

// ── Setup: single intent detail (for the editor drawer) ──
// Returns the CatalogIntent view-model plus the raw versions + current prompt,
// which toCatalogIntent drops but the editor drawer's Versions tab needs.
export interface IntentVersion { versionNo: number; isCurrent: boolean; intentPrompt: string; createdBy: string; createdAt: string }
export interface IntentDetail extends CatalogIntent { id: string; versions: IntentVersion[]; currentPrompt: string }
export async function getIntentDetail(id: string): Promise<{ data: IntentDetail | null; live: boolean }> {
  return liveRead<IntentDetail | null>(async () => {
    const { data } = await axiosInstance.get(Voice_Setup_Intent_Url(id));
    const row = (data?.data?.intent ?? data?.intent) as CatalogDetailRow | undefined;
    if (!row) return null;
    const versions = (row.versions ?? []) as IntentVersion[];
    return {
      ...toCatalogIntent(row, row),
      id: row.id,
      versions,
      currentPrompt: versions.find(v => v.isCurrent)?.intentPrompt ?? '',
    };
  }, null);
}

// ── Setup: create an intent (Softknack authors; identity immutable after) ──
export async function createIntent(input: {
  name: string; ownerKind: 'PRODUCT' | 'VERTICAL'; ownerRef: string; spineTool: string;
  category?: string; description?: string;
  // One-shot config: cut v1 with this prompt + slot template + enrichment.
  intentPrompt?: string; template?: Record<string, unknown>; enrichment?: string[];
}) {
  const { data } = await axiosInstance.post(Voice_Setup_Intents_Url, input);
  return data?.data?.intent ?? data;
}

// ── Setup: intent editor writes (drawer) ──
// Add one enrichment tool to an intent (POST /voice-setup/intents/:id/enrichment).
export async function addEnrichment(intentId: string, toolName: string) {
  const { data } = await axiosInstance.post(Voice_Setup_Intent_Enrichment_Url(intentId), { toolName });
  return data?.data?.binding ?? data;
}

// Cut a new prompt version and set it current (POST /voice-setup/intents/:id/versions).
export async function cutVersion(intentId: string, intentPrompt: string, template?: Record<string, unknown>) {
  const { data } = await axiosInstance.post(Voice_Setup_Intent_Versions_Url(intentId), { intentPrompt, template });
  return data?.data?.version ?? data;
}

// Rollback / set an existing version current (PUT /voice-setup/intents/:id/current-version).
export async function setCurrentVersion(intentId: string, versionNo: number) {
  const { data } = await axiosInstance.put(Voice_Setup_Intent_CurrentVersion_Url(intentId), { versionNo });
  return data?.data?.version ?? data;
}

// ── Controls: agent settings (persona / hours / escalation / csat / notif) ──
// The screen renders a nested settings object; the backend owns the same shape
// under { ok, data: { settings } }. Fallback mirrors the screen's initial state.
export interface AgentSettings {
  persona: { name: string; voice: string; hold: string; language?: string };
  hours: Record<string, unknown>;
  escalation: { turnLimit: string; tier1: string; transfer: string };
  csat: { enabled: boolean; resolvedOnly: boolean; alertBelow: string };
  notif: Record<string, boolean>;
}
// Neutral empty form template (NOT another org's data) — the controlled Settings
// form needs a defined shape to render before the live GET resolves / if it fails.
const AGENT_SETTINGS_EMPTY: AgentSettings = {
  persona: { name: '', voice: '', hold: '' },
  hours: {},
  escalation: { turnLimit: '', tier1: '', transfer: '' },
  csat: { enabled: false, resolvedOnly: false, alertBelow: '' },
  notif: {},
};

export async function getSettings(): Promise<{ data: AgentSettings; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Controls_Settings_Url);
    return (data?.data?.settings ?? data?.settings ?? data?.data ?? data) as AgentSettings;
  }, AGENT_SETTINGS_EMPTY);
}

export async function updateSettings(patch: Partial<AgentSettings>) {
  const { data } = await axiosInstance.put(Voice_Controls_Settings_Url, patch);
  return data?.data?.settings ?? data;
}

// ── Setup: router prompt (one global versioned orchestration prompt) ──
// FE version shape the RouterScreen renders. `date` is a display string; the
// backend sends an ISO createdAt, so we humanize it into the same slot.
export interface RouterVersion { v: number; date: string; by: string; note: string; isCurrent: boolean; body: string; }
interface RouterVersionRow {
  versionNo: number; createdAt: string; createdBy: string; note: string | null;
  isCurrent: boolean; body: string;
}
function toRouterVersion(r: RouterVersionRow): RouterVersion {
  return { v: r.versionNo, date: timeAgo(r.createdAt), by: r.createdBy, note: r.note ?? '', isCurrent: r.isCurrent, body: r.body };
}

export async function listRouterVersions(): Promise<{ data: RouterVersion[]; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Setup_Router_Prompt_Url);
    const rows = (data?.data?.versions ?? data?.versions ?? []) as RouterVersionRow[];
    return rows.map(toRouterVersion);
  }, [] as RouterVersion[]);
}

// Cut a new router-prompt version (POST) — backend sets it current.
export async function createRouterVersion(body: string, note?: string) {
  const { data } = await axiosInstance.post(Voice_Setup_Router_Prompt_Url, { body, note });
  return data?.data?.version ?? data;
}

// Set an existing router-prompt version current (PUT /router-prompt/current).
export async function setRouterCurrent(versionNo: number) {
  const { data } = await axiosInstance.put(Voice_Setup_Router_Prompt_Current_Url, { versionNo });
  return data?.data?.version ?? data;
}

// ── Setup: provider & pipeline config (platform default + per-org overrides) ──
// FE pipe shape ProvidersScreen renders (PIPE_DEFAULT). Backend uses provider
// slugs + separate mode/version fields; we flatten to the one pipe object here.
type Pipe = typeof PIPE_DEFAULT;
interface PipeConfigRow {
  mode: string; sttProvider: string | null; llmProvider: string | null;
  ttsProvider: string | null; unifiedProvider: string | null; versionNo: number;
}
function toPipe(r: PipeConfigRow): Pipe {
  return {
    mode: r.mode,
    stt: r.sttProvider ?? PIPE_DEFAULT.stt,
    llm: r.llmProvider ?? PIPE_DEFAULT.llm,
    tts: r.ttsProvider ?? PIPE_DEFAULT.tts,
    unified: r.unifiedProvider ?? PIPE_DEFAULT.unified,
    ver: r.versionNo,
  };
}
// FE pipe → backend save body. Only send the fields the mode actually uses, so
// the stored row honestly reflects the pipeline: UNIFIED = one provider (no
// stt/llm/tts); COMPOSABLE = stt+llm+tts (no unified). Avoids persisting a
// meaningless "reasoning LLM" alongside a unified voice-to-voice model.
function fromPipe(p: Pipe) {
  if (p.mode === 'UNIFIED') {
    return { mode: p.mode, sttProvider: null, llmProvider: null, ttsProvider: null, unifiedProvider: p.unified };
  }
  return { mode: p.mode, sttProvider: p.stt, llmProvider: p.llm, ttsProvider: p.tts, unifiedProvider: null };
}

// The "Configuration for" org picker. Real orgs are platform-owned (SSOT) —
// fetched platform-direct (like /v1/me) via GET /v1/me/admin/orgs, platform_admin
// -gated. No mock fallback: on failure the picker is empty (truthful), never fake orgs.
export interface AdminOrg { id: string; name: string }
export async function listAdminOrgs(): Promise<{ data: AdminOrg[]; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Platform_Admin_Orgs_Url);
    const rows = (data?.orgs ?? data?.items ?? data?.data?.orgs ?? data?.data ?? []) as any[];
    return rows.map((o) => ({ id: o.id, name: o.name ?? o.id }));
  }, [] as AdminOrg[]);
}

// ── Phone & KYC (staff): link a KYC Plivo already accepted to an org ──
export interface PlivoKyc { id: string; alias: string; status: string; created_at: string | null }
export interface OrgNumberJourney {
  mode: 'LOCAL' | 'PLIVO';
  step: string;
  kyc_status: 'NONE' | 'SUBMITTED' | 'ACCEPTED' | 'REJECTED';
  compliance_id: string | null;
  business_name: string | null;
  chosen_number: string | null;
  is_live: boolean;
}

export async function listPlivoKycs(): Promise<{ data: PlivoKyc[]; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Admin_Plivo_Kycs_Url);
    return (data?.data ?? []) as PlivoKyc[];
  }, [] as PlivoKyc[]);
}

export async function getOrgNumberJourney(orgId: string): Promise<{ data: OrgNumberJourney | null; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Admin_Org_Number_Url(orgId));
    return (data?.data ?? null) as OrgNumberJourney | null;
  }, null as OrgNumberJourney | null);
}

export async function linkOrgKyc(orgId: string, complianceId: string): Promise<OrgNumberJourney> {
  const { data } = await axiosInstance.post(Voice_Admin_Org_Kyc_Link_Url(orgId), { compliance_id: complianceId });
  return data?.data as OrgNumberJourney;
}

// Resolve ONE org's provider config (backend is one-org-at-a-time via ?orgId=).
// Returns the platform default + this org's override (null = inheriting). No
// orgId → the default only (the "Default configuration" view).
export async function getProviderConfig(orgId?: string): Promise<{ data: { default: Pipe; override: Pipe | null }; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Setup_Providers_Url, { params: orgId ? { orgId } : undefined });
    const payload = (data?.data ?? data) as { default: PipeConfigRow | null; override: PipeConfigRow | null };
    return {
      default: payload.default ? toPipe(payload.default) : { ...PIPE_DEFAULT },
      override: payload.override ? toPipe(payload.override) : null,
    };
    // No mock org overrides on failure: neutral default pipe + no override (inherit).
  }, { default: { ...PIPE_DEFAULT }, override: null });
}

// Save the platform default (cuts a new default version).
export async function saveDefault(pipe: Pipe) {
  const { data } = await axiosInstance.put(Voice_Setup_Providers_Default_Url, fromPipe(pipe));
  return data?.data ?? data;
}

// Save a per-org override (cuts a new override version).
export async function saveOverride(orgId: string, pipe: Pipe) {
  const { data } = await axiosInstance.put(Voice_Setup_Providers_Override_Url(orgId), fromPipe(pipe));
  return data?.data ?? data;
}

// Reset an org back to inheriting the platform default.
export async function resetOverride(orgId: string) {
  const { data } = await axiosInstance.delete(Voice_Setup_Providers_Override_Url(orgId));
  return data?.data ?? data;
}

// ── Call data: call log list + KPI strip ──
// Backend list item (FrontendCallListItem) → the FE CallRow the log renders.
interface CallListItemRow {
  callId: string; callerNumber: string; callerName?: string | null; status: string;
  startedAt: string; endedAt: string | null; durationSeconds: number | null;
  disposition: string | null; products: string[];
  upsell: { offered: boolean; offeredN: number; accepted: boolean; acceptedN: number; hitPct: number };
}
// Backend disposition/status → the FE outcome enum the pills switch on.
function toOutcome(row: CallListItemRow): CallRow['outcome'] {
  const s = (row.disposition ?? row.status ?? '').toUpperCase();
  if (s.includes('MISS')) return 'missed';
  if (s.includes('DROP') || s.includes('ABANDON')) return 'caller_dropped';
  return 'completed';
}
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '');
}
function toCallRow(r: CallListItemRow): CallRow {
  const durSec = r.durationSeconds ?? 0;
  // Upsell rollup: FE renders rupee amounts, backend rollup is counts+hitPct.
  // Amounts aren't in this list payload, so amounts stay 0 and only counts show.
  const upsell = r.upsell && r.upsell.offeredN > 0
    ? { acc: 0, off: 0, pct: r.upsell.hitPct, nAcc: r.upsell.acceptedN, nOff: r.upsell.offeredN }
    : null;
  // Show the CRM name when we know it, fall back to the number (r.callerName is
  // resolved server-side, null when unknown).
  const display = (r.callerName && r.callerName.trim()) || r.callerNumber;
  return {
    id: r.callId,
    contact: display,
    initials: initialsOf(display) || '?',
    // Real direction from the backend (SKVOICE-OUTBOUND §3); was hardcoded.
    dir: (r as any).direction === 'outbound' ? 'outbound' : 'inbound',
    dur: durSec > 0 ? fmtDur(durSec) : null,
    durSec,
    outcome: toOutcome(r),
    products: r.products ?? [],
    upsell,
    when: timeAgo(r.startedAt),
  };
}

// Fetch just the header for a call by id — used when the detail is opened without
// a row snapshot (Follow-ups "View call detail", deep-links). Reuses the same
// frontend-read row the list uses, so name-else-number + direction + outcome match.
export interface CallHeader { id: string; contact: string; dir: 'inbound' | 'outbound'; outcome: CallRow['outcome']; durSec: number }
export async function getCallHeader(callId: string): Promise<CallHeader | null> {
  try {
    const { data } = await axiosInstance.get(Voice_Call_Url(callId));
    const r = (data?.data ?? data) as CallListItemRow;
    if (!r?.callId && !r?.callerNumber) return null;
    const display = (r.callerName && r.callerName.trim()) || r.callerNumber;
    return {
      id: callId,
      contact: display,
      dir: (r as any).direction === 'outbound' ? 'outbound' : 'inbound',
      outcome: toOutcome(r),
      durSec: r.durationSeconds ?? 0,
    };
  } catch {
    return null;
  }
}

export async function listCalls(params?: { status?: string; from?: string; to?: string; caller?: string; limit?: number; cursor?: string }): Promise<{ data: CallRow[]; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Calls_Url, { params });
    const rows = (data?.data ?? []) as CallListItemRow[];
    return rows.map(toCallRow);
  }, [] as CallRow[]);
}

// Numbered-page Calls list — server-side filter (caller/status/date) + offset
// paging + total, so search scans ALL calls, not the loaded slice.
export interface CallsPage {
  data: CallRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}
export async function listCallsPaged(params: {
  page: number;
  pageSize: number;
  status?: string;
  caller?: string;
  from?: string;
  to?: string;
}): Promise<{ data: CallsPage; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Calls_Page_Url, { params });
    const rows = (data?.data ?? []) as CallListItemRow[];
    return {
      data: rows.map(toCallRow),
      total: Number(data?.total ?? rows.length),
      page: Number(data?.page ?? params.page),
      pageSize: Number(data?.pageSize ?? params.pageSize),
      totalPages: Number(data?.totalPages ?? 1),
    };
  }, { data: [], total: 0, page: params.page, pageSize: params.pageSize, totalPages: 1 } as CallsPage);
}

// Call-log KPI strip. Backend rollup (FrontendCallLogSummary) → the FE CALL_KPIS shape.
interface CallLogSummaryRow {
  total: number; completedPct: number; completedN: number;
  upsellOffered: number; upsellAccepted: number; hitPct: number;
}
// Zeroed KPI strip — the truthful "no calls yet / read failed" state (not mock totals).
const CALL_KPIS_EMPTY: typeof CALL_KPIS = {
  total: 0, completedPct: 0, completedN: 0,
  offered: 0, offeredAttempts: 0, accepted: 0, hitPct: 0, acceptedN: 0,
};
export async function getCallLogKpis(params?: { from?: string; to?: string }): Promise<{ data: typeof CALL_KPIS; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Calls_Log_Summary_Url, { params });
    const s = (data?.data ?? data) as CallLogSummaryRow;
    return {
      total: s.total,
      completedPct: s.completedPct,
      completedN: s.completedN,
      offered: s.upsellOffered,
      offeredAttempts: s.upsellOffered,
      accepted: s.upsellAccepted,
      hitPct: s.hitPct,
      acceptedN: s.upsellAccepted,
    };
  }, CALL_KPIS_EMPTY);
}

// ── Call data: per-call insights (Call Detail hero) ──
// Backend insights (FrontendCallInsights) → the CALL_DETAIL_01 shape the hero
// renders. Backend `at` fields are ISO Dates for ledgers but ms offsets for
// transcript; the FE wants numeric second-offsets for the ledger `at`, so we
// pass the transcript `at` through and best-effort the ledger `at` to 0.
interface InsightsRow {
  summary: string | null;
  products: Array<{ name: string; kind: string; price: number | null; at: string; response: string }>;
  captured: Array<{ key: string; val: string | null; src: string | null; at: string }>;
  unmatched: Array<{ phrase: string; at: string }>;
  transcript: Array<{ at: number; sp: string; text: string }>;
  offered: number; accepted: number; hitRate: number;
  recordingUrl?: string | null;
  durationSeconds?: number | null;
}
// Detail = the mock-shaped insights PLUS real playback fields (recording + duration)
// that the mock type doesn't carry. Kept separate so the mock type stays untouched.
export type CallDetail = typeof CALL_DETAIL_01 & {
  recordingUrl: string | null;
  durationSeconds: number | null;
};
function toCallDetail(r: InsightsRow): CallDetail {
  const offeredN = r.products.filter(p => p.kind === 'UPSELL' || p.kind === 'CROSS-SELL' || p.kind === 'ASKED').length;
  const acceptedN = r.products.filter(p => p.response === 'interested' || p.response === 'accepted').length;
  return {
    summary: r.summary ?? '',
    products: r.products.map(p => ({ name: p.name, kind: p.kind, price: p.price ?? 0, at: 0, response: p.response })),
    offered: r.offered,
    offeredN,
    accepted: r.accepted,
    acceptedN,
    hitRate: r.hitRate,
    leadValue: r.accepted,
    captured: r.captured.map(x => ({ key: x.key, val: x.val ?? '', src: x.src ?? '', at: 0 })),
    unmatched: r.unmatched.map(x => ({ phrase: x.phrase, at: 0 })),
    // Backend transcript `at` is a MS offset from call start; the UI renders/seeks
    // in SECONDS (fmtDur, compare to playT) — convert ms → s. Normalize the speaker
    // label (backend AGENT/CALLER) to the same display the live view uses.
    transcript: r.transcript.map(t => ({
      at: Math.max(0, Math.floor(t.at / 1000)),
      sp: /agent|suki|sk voice/i.test(t.sp) ? 'SK Voice' : 'Caller',
      text: t.text,
    })),
    recordingUrl: r.recordingUrl ?? null,
    durationSeconds: r.durationSeconds ?? null,
  };
}

// Empty call-detail — the truthful "no insights / read failed" state.
const CALL_DETAIL_EMPTY: CallDetail = {
  summary: '', products: [], offered: 0, offeredN: 0, accepted: 0, acceptedN: 0,
  hitRate: 0, leadValue: 0, captured: [], unmatched: [], transcript: [],
  recordingUrl: null, durationSeconds: null,
};
export async function getCallDetail(callId: string): Promise<{ data: CallDetail; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Call_Insights_Url(callId));
    const row = (data?.data ?? data) as InsightsRow;
    return toCallDetail(row);
  }, CALL_DETAIL_EMPTY);
}

// ── Analytics console: KPI tiles + outcome distribution ──
// Backend payload (FrontendAnalyticsConsole) already matches the FE shape
// (ANALYTICS_KPIS + ANALYTICS_DIST), so this is a thin pass-through with fallback.
interface AnalyticsConsoleRow {
  kpis: Array<{ label: string; value: string; sub: string }>;
  distribution: Array<{ label: string; n: number; pct: number }>;
}
export async function getAnalytics(params?: { from?: string; to?: string }): Promise<{ data: { kpis: typeof ANALYTICS_KPIS; dist: typeof ANALYTICS_DIST }; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Analytics_Console_Url, { params });
    const row = (data?.data ?? data) as AnalyticsConsoleRow;
    return { kpis: row.kpis, dist: row.distribution };
  }, { kpis: [], dist: [] });
}

// ── Live Dashboard (board) ──
// Header identity (name/dial/vertical/accent) + today KPI rollup. Live-now count
// and per-turn transcript come from the SSE liveCallsContext, NOT these reads.
export interface BoardIdentity {
  orgName: string | null;
  dialNumber: string | null;
  vertical: string | null;
  tag: string | null;
  accent: string;
}
export interface BoardSummary {
  live: number;
  callsHandled: number;
  actionsCompleted: number;
  revenue: number; // STUB from backend (0) until per-action amounts exist
  metrics: Record<string, number>;
}
const EMPTY_IDENTITY: BoardIdentity = { orgName: null, dialNumber: null, vertical: null, tag: null, accent: '#2D5CF6' };
const EMPTY_SUMMARY: BoardSummary = { live: 0, callsHandled: 0, actionsCompleted: 0, revenue: 0, metrics: {} };

export async function getBoardIdentity(): Promise<{ data: BoardIdentity; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Board_Identity_Url);
    return (data?.data ?? data) as BoardIdentity;
  }, EMPTY_IDENTITY);
}

export async function getBoardSummary(): Promise<{ data: BoardSummary; live: boolean }> {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Board_Summary_Url);
    return (data?.data ?? data) as BoardSummary;
  }, EMPTY_SUMMARY);
}

// ═══════════════════════════════════════════════════════════════════════════
// OUTBOUND capability (SKVOICE-OUTBOUND-001). liveRead → truthful empty on
// backend failure (no mock fallback), matching the rest of this service.
// ═══════════════════════════════════════════════════════════════════════════
import {
  Voice_Outbound_Intents_Url,
  Voice_Outbound_Intent_Url,
  Voice_Outbound_Intent_Preview_Url,
  Voice_Outbound_Contacts_Url,
  Voice_Outbound_Settings_Url,
  Voice_Outbound_ReasonWindows_Url,
  Voice_Outbound_RetryRules_Url,
  Voice_Outbound_Reasons_Url,
  Voice_Outbound_Reason_Url,
  Voice_Campaigns_Url,
  Voice_Campaign_Url,
  Voice_Campaign_Launch_Url,
  Voice_Dnd_Url,
  Voice_Dnd_Lift_Url,
  Voice_Inbound_Settings_Url,
} from '../../api/endpoints';

export interface OutboundIntentRow {
  id: string; customerId: string; reasonCode: string; regulatoryClass: string;
  status: string; origin: string; callAt: string; expiresAt: string;
  schedulingBasis: string; sourceRef: string | null; attemptNo: number; createdAt: string;
  // Enriched from CRM server-side so the UI shows a name/phone, never the ctc_ id.
  customerName?: string | null; customerPhone?: string | null; customerDisplay?: string | null;
  // Latest attempt outcome + the reason's attempt ceiling (real reads, for the list).
  latestOutcome?: string | null; maxAttempts?: number | null;
}

export async function listOutboundIntents(params?: { status?: string; reason_code?: string; customer_id?: string; from?: string; to?: string }) {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Outbound_Intents_Url, { params });
    return (data?.data?.intents ?? []) as OutboundIntentRow[];
  }, [] as OutboundIntentRow[]);
}

export async function getOutboundIntent(id: string) {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Outbound_Intent_Url(id));
    return data?.data ?? null;
  }, null as any);
}

export async function previewOutboundIntent(body: { customer_id?: string; phone?: string; reason_code: string; source_ref?: string }) {
  const { data } = await axiosInstance.post(Voice_Outbound_Intent_Preview_Url, body);
  return data?.data ?? null;
}

export async function createOutboundIntent(body: { customer_id?: string; phone?: string; customer_name?: string; reason_code: string; source_ref?: string; preferred_language?: string; context_payload?: Record<string, unknown> }) {
  const { data } = await axiosInstance.post(Voice_Outbound_Intents_Url, body);
  return data?.data ?? null;
}

// Customer picker for the New-call modal — search CRM contacts by name OR phone.
export interface OutboundContact { id: string; name: string; phone: string | null }
export async function searchOutboundContacts(q: string): Promise<OutboundContact[]> {
  if (q.trim().length < 2) return [];
  try {
    const { data } = await axiosInstance.get(Voice_Outbound_Contacts_Url, { params: { q: q.trim() } });
    return (data?.data?.contacts ?? []) as OutboundContact[];
  } catch { return []; }
}
// Create-in-place. Phone is unique: a duplicate throws a 409 the caller surfaces.
export async function createOutboundContact(body: { name: string; phone: string }): Promise<OutboundContact> {
  const { data } = await axiosInstance.post(Voice_Outbound_Contacts_Url, body);
  return data?.data?.contact as OutboundContact;
}

export async function getOutboundSettings() {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Outbound_Settings_Url);
    return data?.data ?? null;
  }, null as any);
}

export async function updateOutboundSettings(body: { daily_call_cap?: number; max_concurrency?: number; default_lead_minutes?: number; outbound_enabled?: boolean }) {
  const { data } = await axiosInstance.put(Voice_Outbound_Settings_Url, body);
  return data;
}

export async function updateReasonWindows(windows: Array<{ reason_code: string; window_start: string; window_end: string; lead_minutes: number }>) {
  const { data } = await axiosInstance.put(Voice_Outbound_ReasonWindows_Url, { windows });
  return data;
}

export async function updateRetryRules(rules: Array<{ outcome: string; retry_delay_minutes: number | null; max_retries: number }>) {
  const { data } = await axiosInstance.put(Voice_Outbound_RetryRules_Url, { rules });
  return data;
}

// includeInactive: the Setup console (platform admin) sees + manages inactive
// reasons; the org New-call dropdown omits it and gets active reasons only.
export async function listOutboundReasons(includeInactive = false) {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Outbound_Reasons_Url, includeInactive ? { params: { all: 'true' } } : undefined);
    return (data?.data?.reasons ?? []) as any[];
  }, [] as any[]);
}

export async function createOutboundReason(body: Record<string, unknown>) {
  const { data } = await axiosInstance.post(Voice_Outbound_Reasons_Url, body);
  return data;
}

export async function updateOutboundReason(code: string, patch: Record<string, unknown>) {
  const { data } = await axiosInstance.patch(Voice_Outbound_Reason_Url(code), patch);
  return data;
}

export async function listCampaigns() {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Campaigns_Url);
    return (data?.data?.campaigns ?? []) as any[];
  }, [] as any[]);
}

export async function getCampaign(id: string) {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Campaign_Url(id));
    return data?.data ?? null;
  }, null as any);
}

export async function createCampaign(body: { name: string; reason_code: string; contacts: Array<{ phone: string; name?: string }> }) {
  const { data } = await axiosInstance.post(Voice_Campaigns_Url, body);
  return data?.data ?? null;
}

export async function launchCampaign(id: string) {
  const { data } = await axiosInstance.post(Voice_Campaign_Launch_Url(id), {});
  return data?.data ?? null;
}

export async function listDnd(direction?: string) {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Dnd_Url, { params: direction ? { direction } : undefined });
    return (data?.data?.suppressions ?? []) as any[];
  }, [] as any[]);
}

export async function addDndBlock(body: { customer_id?: string; phone?: string; name?: string; scope: string; direction: string; reason?: string }) {
  const { data } = await axiosInstance.post(Voice_Dnd_Url, body);
  return data?.data ?? null;
}

export async function liftDndBlock(id: string) {
  const { data } = await axiosInstance.post(Voice_Dnd_Lift_Url(id), {});
  return data;
}

export async function getInboundSettings() {
  return liveRead(async () => {
    const { data } = await axiosInstance.get(Voice_Inbound_Settings_Url);
    return data?.data?.settings ?? null;
  }, null as any);
}

export async function updateInboundSettings(body: Record<string, unknown>) {
  const { data } = await axiosInstance.put(Voice_Inbound_Settings_Url, body);
  return data;
}
